/**
 * The area audit's vocabulary and the readers for one finding.
 *
 * `@/types/tina` owns the wire shapes (`AreaAuditFindingRow`, `AreaAuditEvidence`); this
 * module owns what the curator surface needs on top of them — the labels, the per-kind
 * hint, the one-line evidence summary, the two deep links out of a finding, and the
 * reading of a failed request as "not deployed yet". Same split, and the same
 * `Record<Union, string>` discipline, as `@/lib/tina/venue-audit`: adding a member to
 * `AREA_AUDIT_KINDS` without labelling it here is a compile error.
 *
 * Every render path degrades instead of throwing. `kind` and `evidence` arrive as wire data
 * from an orchestrator deploy that may be newer (or older) than this build, so an unknown
 * kind falls back to its raw string and an evidence object without the kind's own field
 * falls back to listing whatever scalar fields it does carry.
 */
import dayjs from "dayjs";

import { venueAuditVenueHref } from "@/lib/tina/venue-audit";
import {
  AREA_AUDIT_KINDS,
  AREA_AUDIT_SEVERITIES,
  AREA_AUDIT_STATUSES,
  type AreaAuditEvidence,
  type AreaAuditFindingRow,
  type AreaAuditFindingStatus,
  type AreaAuditKind,
  type AreaAuditSeverity,
  type AreaAuditStatus,
} from "@/types/tina";

/** Short label for a kind — what the "What" column and the count strip show. */
export const AREA_AUDIT_KIND_LABEL: Record<AreaAuditKind, string> = {
  area_mislabelled_venue: "Probably mislabelled venue",
  area_unplaced_venue: "Venue without an area",
  area_venue_on_part: "Venue on a part",
  area_without_part: "Area without a part",
  area_small_part: "Small part",
  area_city_held_back: "City held back",
  area_empty: "Empty area",
  area_auto_review: "Auto area to review",
  area_part_assigned: "Part set by the nightly",
  area_border_dropped: "Border dropped by the nightly",
};

/**
 * What the curator is being asked to judge, per kind. Shown once beside the kind filter
 * and as the "What" column's tooltip, like the venue audit's hints.
 */
export const AREA_AUDIT_KIND_HINT: Record<AreaAuditKind, string> = {
  area_mislabelled_venue:
    "The venue's point lies inside another area's shape, its own name is not in its address, and no OSM shape shows the label is only broader or narrower. Open the venue and judge: move it to the area that holds it, or dismiss if the label is right.",
  area_unplaced_venue:
    "A live venue with a usable point, in a city that has areas, sits in no area. Open the venue and give it its area.",
  area_venue_on_part:
    "The venue's area is a part — a group of areas — rather than an area. Move it to the area inside that part where it actually is.",
  area_without_part:
    "An area of a city that has parts, with no part above it. Open the area and pick its part, or merge it into a neighbour.",
  area_small_part:
    "A part with fewer areas than the minimum a part should hold. Judge whether it should take more areas or merge into a neighbouring part.",
  area_city_held_back:
    "The area step held the whole city back: its part plan looks half-applied, so no area in it gets a part until the plan is fixed. Read the reason.",
  area_empty:
    "An area with no live venue in it. Merge it into a neighbour if nothing belongs there; dismiss if it is a real area that simply has no venue yet.",
  area_auto_review:
    "Tina's live area picker created this area and no curator has checked it yet. Open it, check its name and shape, and mark it reviewed.",
  area_part_assigned:
    "For information: tonight the nightly gave this area a part by itself, by the nearest-part rule. Check the part is right (for example a town near a border), fix it on the area page if not, then dismiss.",
  area_border_dropped:
    "For information: tonight the nightly dropped this area's stored border because it was its part's outline. Check the area's shape on the map, then dismiss.",
};

/** Kind label that survives a kind this build does not know. */
export const areaAuditKindLabel = (kind: string): string =>
  kind in AREA_AUDIT_KIND_LABEL
    ? AREA_AUDIT_KIND_LABEL[kind as AreaAuditKind]
    : kind;

/** Kind hint, or `undefined` for a kind this build does not know. */
export const areaAuditKindHint = (kind: string): string | undefined =>
  kind in AREA_AUDIT_KIND_HINT
    ? AREA_AUDIT_KIND_HINT[kind as AreaAuditKind]
    : undefined;

export const AREA_AUDIT_STATUS_LABEL: Record<AreaAuditStatus, string> = {
  open: "Open",
  dismissed: "Dismissed",
  resolved: "Fixed itself",
  all: "Everything",
};

export const AREA_AUDIT_STATUS_OPTIONS: {
  value: AreaAuditStatus;
  label: string;
}[] = AREA_AUDIT_STATUSES.map((status) => ({
  value: status,
  label: AREA_AUDIT_STATUS_LABEL[status],
}));

export const AREA_AUDIT_SEVERITY_LABEL: Record<AreaAuditSeverity, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

export const AREA_AUDIT_SEVERITY_OPTIONS: {
  value: AreaAuditSeverity;
  label: string;
}[] = AREA_AUDIT_SEVERITIES.map((severity) => ({
  value: severity,
  label: AREA_AUDIT_SEVERITY_LABEL[severity],
}));

/**
 * Narrowed on read rather than trusted: these values come back from `localStorage`, and
 * the route types `kind`, `severity` and `status` as Literals — an unknown value is a 422,
 * not an empty page.
 */
export const isAreaAuditKind = (value: string | null): value is AreaAuditKind =>
  value !== null && AREA_AUDIT_KINDS.includes(value as AreaAuditKind);

export const isAreaAuditSeverity = (
  value: string | null,
): value is AreaAuditSeverity =>
  value !== null && AREA_AUDIT_SEVERITIES.includes(value as AreaAuditSeverity);

export const isAreaAuditStatus = (
  value: string | null,
): value is AreaAuditStatus =>
  value !== null && AREA_AUDIT_STATUSES.includes(value as AreaAuditStatus);

/**
 * A row's lifecycle state: the route's `status`, or — from a route that does not send it —
 * what its timestamps say, a dismissal first.
 */
export const areaAuditRowStatus = (
  row: AreaAuditFindingRow,
): AreaAuditFindingStatus => {
  if (
    row.status === "open" ||
    row.status === "dismissed" ||
    row.status === "resolved"
  )
    return row.status;
  if (row.dismissed_at) return "dismissed";
  if (row.resolved_at) return "resolved";
  return "open";
};

/** The curator's dismissal note, under either name the route may use for it. */
export const areaAuditDismissNote = (row: AreaAuditFindingRow): string | null =>
  row.dismissed_note || row.dismissed_reason || null;

/** Where a curator opens the area: its `/areas` edit page. */
export const areaAuditAreaHref = (areaId: string): string =>
  `/areas/edit/${encodeURIComponent(areaId)}`;

/** Where a curator opens the venue: the same page the venue audit links. */
export const areaAuditVenueHref = (venueId: string): string =>
  venueAuditVenueHref(venueId);

const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

const count = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const plural = (n: number, one: string, many: string): string =>
  `${n} ${n === 1 ? one : many}`;

/**
 * Every scalar field of the evidence as `key: value`, for a kind this build does not know
 * or an evidence object whose kind's own field did not arrive. Nested objects are left out:
 * a cell is one line.
 */
export const areaAuditEvidenceFields = (evidence: AreaAuditEvidence): string =>
  Object.entries(evidence ?? {})
    .flatMap(([key, value]) => {
      if (typeof value === "string")
        return value.trim() ? [`${key}: ${value.trim()}`] : [];
      if (typeof value === "number" || typeof value === "boolean")
        return [`${key}: ${String(value)}`];
      if (
        Array.isArray(value) &&
        value.length > 0 &&
        value.every((item) => typeof item !== "object")
      )
        return [`${key}: ${value.join(", ")}`];
      return [];
    })
    .join("  ·  ");

/**
 * The kind's own one-line reading of its evidence, or `null` when the field it needs did
 * not arrive — which is what lets `areaAuditEvidenceSummary` fall back to the raw fields.
 */
const kindSummary = (row: AreaAuditFindingRow): string | null => {
  const evidence = row.evidence ?? {};
  switch (row.kind) {
    case "area_mislabelled_venue": {
      const holding = text(evidence.holding_area);
      if (!holding) return null;
      const label = text(evidence.label_area) ?? text(row.area_name);
      const distance = count(evidence.distance_m);
      const address = text(evidence.address);
      return [
        label
          ? `labelled ${label}, but inside ${holding}`
          : `inside ${holding}`,
        distance === null
          ? null
          : `${Math.round(distance)} m from ${label ?? "its area"}'s centre`,
        address,
      ]
        .filter(Boolean)
        .join("  ·  ");
    }
    case "area_unplaced_venue": {
      if (!("label" in evidence)) return null;
      const label = text(evidence.label);
      return label ? `its area label is "${label}"` : "no area label";
    }
    case "area_venue_on_part": {
      const part = text(evidence.part_name) ?? text(row.area_name);
      return part ? `placed on the part ${part}` : null;
    }
    case "area_without_part": {
      if (evidence.part_gone === true) return "its part is gone";
      const parts = count(evidence.city_parts);
      return parts === null
        ? null
        : `the city has ${plural(parts, "part", "parts")}`;
    }
    case "area_small_part": {
      const children = count(evidence.children);
      return children === null
        ? null
        : `holds ${plural(children, "area", "areas")}`;
    }
    case "area_city_held_back":
      return text(evidence.reason);
    case "area_empty":
      return "no live venue";
    case "area_part_assigned": {
      const part = text(evidence.part_name);
      return part ? `put into the part ${part}` : null;
    }
    case "area_border_dropped": {
      const part = text(evidence.part_name);
      return part
        ? `border dropped, it was ${part}'s outline`
        : "border dropped";
    }
    case "area_auto_review": {
      const created = text(evidence.created_at);
      if (!created) return "created by Tina, not yet reviewed";
      const at = dayjs(created);
      return at.isValid()
        ? `created by Tina on ${at.format("DD/MM/YYYY")}`
        : `created by Tina: ${created}`;
    }
    default:
      return null;
  }
};

/**
 * One line a curator can judge a row from without opening anything: the kind's own
 * reading of its evidence, else every scalar evidence field, else `""` (the cell shows a
 * dash). Never throws on an odd evidence shape.
 */
export const areaAuditEvidenceSummary = (row: AreaAuditFindingRow): string =>
  kindSummary(row) ?? areaAuditEvidenceFields(row.evidence ?? {});

/** The HTTP status of a failed request, whichever error shape carried it. */
export const httpStatusOf = (error: unknown): number | undefined => {
  if (typeof error !== "object" || error === null) return undefined;
  const e = error as {
    statusCode?: unknown;
    status?: unknown;
    response?: { status?: unknown };
  };
  for (const value of [e.statusCode, e.response?.status, e.status]) {
    if (typeof value === "number") return value;
  }
  return undefined;
};

/**
 * True when the findings route itself is missing — the orchestrator answers 404 until the
 * Tina side of the area audit is deployed. The tab then says "not available yet" rather
 * than rendering an empty table that would read as "no findings".
 */
export const isAreaAuditNotDeployed = (error: unknown): boolean =>
  httpStatusOf(error) === 404;
